import { useState, useEffect } from "react";
import { motion } from "framer-motion";

export default function HeroSection() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState<
    Array<{ x: number; y: number; delay: number; size: number }>
  >([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    // Generate floating particles
    const newParticles = Array.from({ length: 40 }, (_, _i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 3,
      size: Math.random() * 4 + 2,
    }));
    setParticles(newParticles);

    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  

  const titleText = "Designing Tomorrow's";
  const subtitleText = "Workspaces";

  return (
    <section id="hero" className="relative h-screen overflow-hidden">
      {/* Background Image with exact parallax effect */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/0fb2ebc2a784c483d38d2e790da15b4e/61ac07a8c8f52b153769ed4979676e86.webp')`,
          transform: `translateY(${scrollY * 0.5}px)`,
        }}
      />

      {/* Light Blue Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 via-blue-500/15 to-blue-600/25" />

      {/* Mouse Following Light Source */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(600px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(173, 216, 230, 0.15), rgba(135, 206, 235, 0.08), transparent 70%)`,
        }}
        animate={{
          opacity: [0.6, 0.9, 0.6],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Reduced Blue Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#225a96]/40 via-[#1a4670]/30 to-[#0f2d47]/50" />

      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Floating geometric shapes with reduced opacity */}
        <div className="absolute top-16 right-16 w-64 h-64 bg-gradient-to-br from-[#d02b37]/8 to-[#225a96]/5 rounded-full blur-2xl animate-pulse" />
        <div className="absolute bottom-32 left-16 w-48 h-48 bg-gradient-to-tr from-[#225a96]/10 to-[#0f2d47]/8 rounded-full blur-xl animate-float" />
        <div className="absolute top-1/3 left-1/3 w-32 h-32 bg-gradient-to-r from-[#d02b37]/5 to-[#1a4670]/8 rounded-full blur-lg" />

        {/* Interactive mouse follower */}
        <motion.div
          className="absolute w-[400px] h-[400px] bg-gradient-radial from-[#d02b37]/10 via-[#225a96]/5 to-transparent rounded-full blur-2xl pointer-events-none"
          animate={{
            x: mousePosition.x * 5,
            y: mousePosition.y * 5,
          }}
          transition={{ type: "spring", damping: 30, stiffness: 200 }}
          style={{
            transform: "translate(-50%, -50%)",
          }}
        />

        {/* Floating particles */}
        <div className="absolute inset-0">
          {particles.map((particle, index) => (
            <motion.div
              key={index}
              className="absolute bg-gradient-to-r from-[#d02b37]/40 to-[#225a96]/30 rounded-full"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.2, 0.6, 0.2],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                delay: particle.delay,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>

        {/* Grid pattern overlay with reduced opacity */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(245,245,245,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(245,245,245,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Additional depth layers with reduced opacity */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f2d47]/10 via-transparent to-[#225a96]/8" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a4670]/8 via-transparent to-[#225a96]/10" />
      </div>

      {/* Main Content with exact scroll effect */}
      <div className="relative z-10 flex items-center h-screen">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            style={{
              transform: `translateY(${scrollY * -0.2}px)`,
            }}
          >
            {/* Animated Title */}
            <div className="mb-6 mt-6">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                {titleText.split("").map((char, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, y: 50, rotateX: -90 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.4 + index * 0.06,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    className="inline-block bg-gradient-to-r from-white via-[#f5f5f5] to-[#e5e5e5] bg-clip-text "
                  >
                    {char === " " ? "\u00A0" : char}
                  </motion.span>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -80 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 1.2 }}
                className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mt-2"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                {subtitleText.split("").map((char, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.5, rotateY: 180 }}
                    animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                    transition={{
                      duration: 0.6,
                      delay: 1.4 + index * 0.08,
                      type: "spring",
                      stiffness: 200,
                    }}
                    className="inline-block pb-2 bg-gradient-to-r from-[#d02b37] via-[#ff6b7a] to-[#d02b37] bg-clip-text text-transparent"
                  >
                    {char}
                  </motion.span>
                ))}
              </motion.div>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 2 }}
              className="text-base md:text-lg text-[#f5f5f5] mb-8 max-w-2xl leading-relaxed"
              style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
            >
              Transform your workspace with our premium collection of luxury
              office furniture. Where innovation meets elegance, and
              productivity finds its perfect environment.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 2.3 }}
              className="flex flex-col sm:flex-row gap-4 mb-8"
            >
              <motion.button
                onClick={() => {
                  const element = document.getElementById("products");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                whileHover={{
                  scale: 1.05,
                  boxShadow:
                    "0 15px 30px rgba(208, 43, 55, 0.4), 0 0 25px rgba(208, 43, 55, 0.2)",
                }}
                whileTap={{ scale: 0.98 }}
                className="group relative overflow-hidden bg-gradient-to-r from-[#d02b37] to-[#ff6b7a] text-white px-6 py-2.5 text-sm font-bold rounded-2xl shadow-xl transition-all duration-400 whitespace-nowrap"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                <motion.div className="absolute inset-0 bg-gradient-to-r from-[#ff6b7a] to-[#d02b37] opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                <span className="relative z-10 flex items-center gap-2">
                  Explore Our Collection
                  <motion.i
                    className="ri-arrow-right-line text-lg"
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </span>
              </motion.button>

             
            </motion.div>

            {/* Updated Stats Cards to match reference */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 2.6 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl"
            >
              {[
                {
                  number: "500+",
                  label: "Projects Completed",
                  icon: "ri-building-line",
                },
                {
                  number: "50+",
                  label: "Premium Clients",
                  icon: "ri-user-star-line",
                },
                {
                  number: "15+",
                  label: "Years Experience",
                  icon: "ri-time-line",
                },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 2.8 + index * 0.2 }}
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    boxShadow: "0 15px 30px rgba(255, 255, 255, 0.1)",
                  }}
                  className="text-center group cursor-pointer p-3 rounded-2xl backdrop-blur-sm bg-white/5 border border-white/10 transition-all duration-500 hover:border-[#d02b37]/30"
                >
                  <motion.div
                    className="text-2xl md:text-3xl font-bold text-[#d02b37] mb-2"
                    style={{
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                    whileHover={{ scale: 1.1 }}
                  >
                    {stat.number}
                  </motion.div>
                  <div
                    className="text-[#f5f5f5] text-xs uppercase tracking-wide group-hover:text-white transition-colors duration-300"
                    style={{
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  >
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Enhanced Scroll Indicator with exact scroll effect */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 3.2 }}
        className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-20"
        style={{
          transform: `translateX(-50%) translateY(${scrollY * -0.1}px)`,
        }}
      >
        <motion.div
          className="flex flex-col items-center text-[#f5f5f0] cursor-pointer group"
          whileHover={{ scale: 1.1 }}
        >
          <motion.span
            className="text-xs mb-1 uppercase tracking-wide group-hover:text-[#d02b37] transition-colors duration-400"
            style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2.5, repeat: Infinity }}
          >
            Discover More
          </motion.span>
          <div className="w-6 h-12 border-2 border-[#f5f5f5] rounded-full flex justify-center group-hover:border-[#d02b37] transition-colors backdrop-blur-sm glass-effect">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-1.5 h-3 bg-gradient-to-b from-[#d02b37] to-[#ff6b7a] rounded-full mt-1.5 shadow-md"
            />
          </div>
        </motion.div>
      </motion.div>

      {/* Floating Brand Elements with exact scroll effect */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8, rotate: -30 }}
        animate={{ opacity: 0.1, scale: 1, rotate: 0 }}
        transition={{ duration: 1.5, delay: 1 }}
        className="absolute top-1/4 right-16 hidden lg:block"
        style={{
          transform: `translateY(${scrollY * -0.05}px) rotate(${
            scrollY * -0.03
          }deg)`,
        }}
        whileHover={{ opacity: 0.3, scale: 1.1, rotate: 10 }}
      >
        <img
          alt="Prime Edge Logo"
          className="w-24 h-24 filter grayscale opacity-20"
          src="https://static.readdy.ai/image/0fb2ebc2a784c483d38d2e790da15b4e/7d0800ddf0a53dc8c09418d60a71af4e.png"
        />
      </motion.div>

      {/* Additional floating elements */}
      <motion.div
        className="absolute top-1/2 left-8 w-16 h-16 bg-gradient-to-br from-[#d02b37]/8 to-[#225a96]/5 rounded-full blur-lg"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.1, 0.3, 0.1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute bottom-1/4 right-8 w-12 h-12 bg-gradient-to-tl from-[#225a96]/10 to-[#0f2d47]/5 rounded-full blur-md"
        animate={{
          y: [-15, 15, -15],
          x: [-8, 8, -8],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </section>
  );
}
